import React from "react";
import { Alert, Box, Chip, Paper, Typography } from "@mui/material";
import type { OwnerVoiceSettings, VoiceStatus } from "../../api/voiceSettings";

const STATUS_COLORS: Record<VoiceStatus, { bg: string; color: string }> = {
  connected: { bg: "#F0FDF4", color: "#15803D" },
  sync_pending: { bg: "#FFFBEB", color: "#B45309" },
  not_configured: { bg: "#F8FAFC", color: "#64748B" },
  disabled: { bg: "#F8FAFC", color: "#64748B" },
  sync_error: { bg: "#FEF2F2", color: "#DC2626" },
};

function phoneStatusLabel(status: string): string {
  if (status === "configured") return "Configured";
  if (status === "manual_setup_required") return "Manual setup required";
  return "Setup needed";
}

export function VoiceStatusSummary({
  settings,
}: {
  settings: OwnerVoiceSettings;
}) {
  const colors = STATUS_COLORS[settings.status] ?? STATUS_COLORS.not_configured;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "14px",
        border: "1px solid #E2E8F0",
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, color: "#0F172A" }}>
            Voice channel
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: "0.9rem", color: "#475569" }}>
            Calls use the same assistant style after voice changes are synced.
          </Typography>
        </Box>
        <Chip
          label={settings.status_label}
          size="small"
          sx={{
            backgroundColor: colors.bg,
            color: colors.color,
            fontWeight: 700,
          }}
        />
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", mb: 0.5 }}>
            Phone
          </Typography>
          <Typography sx={{ fontSize: "0.88rem", color: "#0F172A" }}>
            {settings.phone_number ?? "Not configured"}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8", mb: 0.5 }}>
            Phone status
          </Typography>
          <Typography sx={{ fontSize: "0.88rem", color: "#0F172A" }}>
            {phoneStatusLabel(settings.phone_status)}
          </Typography>
        </Box>
      </Box>

      {settings.safe_error && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {settings.safe_error}
        </Alert>
      )}

      {settings.status === "sync_pending" && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Changes need to be synced by Setin before they go live.
        </Alert>
      )}
    </Paper>
  );
}
